/**
 * « Ta trajectoire face à celle du gouvernement » (ADR 0011) : déficit et dette, ta partie (trait plein), le budget
 * tendanciel du jeu (tirets) et la trajectoire officielle (pointillés, fourchette en bande). Tout vient du moteur ou de la
 * baseline (`baseline.outlook`, ou à défaut les prévisions `apu.*_forecast*`), avec ⓘ.
 */
import type { Figure, YearState } from "../../../engine/src/types.js";
import { escapeHtml as e, num, pct } from "../format.js";
import { officialOutlook, type OutlookPoint } from "../outlook.js";
import type { Store } from "../state.js";
import { compareChart, type PathSeries } from "./chart.js";
import { srcButton } from "./source-info.js";

interface Ind {
  key: "deficit_pct_gdp" | "debt_pct_gdp";
  title: string;
  /** Valeur affichée (déficit en positif, comme partout dans l'UI). */
  show: (v: number) => number;
  dec: number;
  base: (s: Store) => Figure | undefined;
}

const INDS: Ind[] = [
  { key: "deficit_pct_gdp", title: "Déficit public (% du PIB)", show: (v) => -v, dec: 1, base: (s) => s.content.baseline.apu.deficit_pct_gdp },
  { key: "debt_pct_gdp", title: "Dette publique (% du PIB)", show: (v) => v, dec: 0, base: (s) => s.content.baseline.apu.debt_pct_gdp },
];

/** Point de départ réel (année du chiffre, pas l'année de base : red team v0.2 M8). */
function startPoint(store: Store, ind: Ind): { year: number; value: number } | null {
  const f = ind.base(store);
  if (!f || f.value === null) return null;
  return { year: f.year ?? store.content.baseline.meta.base_year, value: ind.show(f.value) };
}

const pathOf = (years: YearState[], ind: Ind) => years.filter((y) => y.kpis[ind.key] !== undefined).map((y) => ({ year: y.year, value: ind.show(y.kpis[ind.key]!) }));

/**
 * Carte de comparaison. `player` = années simulées de la partie (absent sur « Ce qui existe ») ; `trend` = tendanciel du jeu.
 */
export function outlookCard(store: Store, opts: { player?: YearState[]; trend?: YearState[]; title: string }): string {
  const o = officialOutlook(store.content.baseline);
  const charts: string[] = [];
  const sentences: string[] = [];
  for (const ind of INDS) {
    const start = startPoint(store, ind);
    const withStart = (pts: Array<{ year: number; value: number }>) => (start && pts.length && pts[0]!.year > start.year ? [start, ...pts] : pts);
    const series: PathSeries[] = [];
    if (opts.player?.length) series.push({ label: "ta partie", points: withStart(pathOf(opts.player, ind)), style: "solid", color: "var(--cat-1)" });
    if (opts.trend?.length) series.push({ label: "si rien ne change (jeu)", points: withStart(pathOf(opts.trend, ind)), style: "dashed", color: "var(--cat-2)" });
    const off: OutlookPoint[] | undefined = o?.series[ind.key]?.map((p) => {
      const lo = p.low !== undefined ? ind.show(p.low) : undefined;
      const hi = p.high !== undefined ? ind.show(p.high) : undefined;
      return { year: p.year, value: ind.show(p.value), ...(lo !== undefined && hi !== undefined ? { low: Math.min(lo, hi), high: Math.max(lo, hi) } : {}) };
    });
    const c = compareChart(ind.title, series, off, o?.label ?? "trajectoire officielle", (v) => num(v, ind.dec === 0 ? 0 : 1));
    if (c) charts.push(c);
    // Comparaison chiffrée à la dernière année commune
    const mine = series[0]?.points ?? [];
    const common = off?.filter((p) => mine.some((m) => m.year === p.year)).pop();
    if (common) {
      const m = mine.find((x) => x.year === common.year)!;
      const gap = m.value - common.value;
      sentences.push(`${ind.title.replace(/ \(% du PIB\)/, "")} ${common.year} : ${pct(m.value, ind.dec)} (${series[0]!.label}) contre ${pct(common.value, ind.dec)} pour la ${e(o!.label)}${Math.abs(gap) >= Math.pow(10, -ind.dec) / 2 ? `, soit ${gap > 0 ? "+" : "−"}${num(Math.abs(gap), ind.dec)} pt` : ""}.`);
    }
  }
  if (!charts.length) return "";
  const src = o
    ? srcButton({ title: o.label, sources: o.sources.length ? o.sources : ["TODO"], notes: [o.derived ? "Points reconstruits à partir des prévisions publiées présentes dans la baseline (pas encore de trajectoire complète « outlook »)." : "Trajectoire publiée (baseline.outlook).", "La trajectoire officielle inclut les mesures du budget du gouvernement : elle n'est pas un « si rien ne change »."] }, { inline: true })
    : "";
  return `<div class="card"><h2>${e(opts.title)}${src}</h2>
    ${o ? "" : `<p class="hint">Trajectoire officielle indisponible dans les données chargées.</p>`}
    <div class="cmpcharts">${charts.join("")}</div>
    ${sentences.length ? `<p class="sm">${sentences.join(" ")}</p>` : ""}
    <p class="hint">Départ : dernier chiffre publié (année indiquée sur l'axe). Les courbes du jeu sont simulées par le moteur ; la trajectoire officielle est une prévision du gouvernement, mesures de son budget comprises.</p></div>`;
}
